import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Checkbox,
    Stack,
    Autocomplete,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ShoppingCart as CartIcon,
    LocationOn as LocationIcon,
    ReceiptLong as BillingIcon,
    Payment as PaymentIcon,
    CheckCircle as ConfirmIcon,
} from '@mui/icons-material';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { ecommerceApi, cartHelpers } from '../services/ecommerceApi';
import { useCart } from '../hooks/useCart';
import { useNotification } from '../components/common/GlobalNotifications';
import { useAuth } from '../hooks/useAuth';
import PhoneInput from '../components/common/PhoneInput';
import CartItemLine from '../components/store/CartItemLine';
import CartOrderSummary from '../components/store/CartOrderSummary';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const initialShipping = (user) => ({
    first_name: user?.name || '',
    last_name: user?.last_name || '',
    company: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'México',
    phone: user?.phone || '',
});

const initialBilling = (user) => ({
    first_name: user?.name || '',
    last_name: user?.last_name || '',
    company: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'México',
    phone: user?.phone || '',
    person_type: 'fisica',
    business_name: '',
    rfc: '',
    cfdi_use: '',
    cfdi_payment_method: '',
    cfdi_payment_form: '',
});

const REQUIRED_SHIPPING = ['first_name', 'last_name', 'address_line_1', 'city', i18n.t('hardcoded.components_auth_register.state'), 'postal_code', 'country'];
const REQUIRED_BILLING = [
    'person_type',
    'business_name',
    'address_line_1',
    'city',
    i18n.t('hardcoded.components_auth_register.state'),
    'country',
    'postal_code',
    'rfc',
    'cfdi_use',
    'cfdi_payment_method',
    'cfdi_payment_form',
];

const VEHICLE_VALIDATION_REGEX = /(assigned vehicle|vehicle.*required|requires an assigned vehicle)/i;

const CFDI_USE_OPTIONS = [
    i18n.t('hardcoded.pages_checkoutpage.g01_-_adquisicion_de_mercancias'),
    i18n.t('hardcoded.pages_checkoutpage.g02_-_devoluciones_descuentos_o'),
    i18n.t('hardcoded.pages_checkoutpage.g03_-_gastos_en_general'),
    i18n.t('hardcoded.pages_checkoutpage.i01_-_construcciones'),
    i18n.t('hardcoded.pages_checkoutpage.i02_-_mobiliario_y_equipo'),
    i18n.t('hardcoded.pages_checkoutpage.i03_-_equipo_de_transporte'),
    i18n.t('hardcoded.pages_checkoutpage.i04_-_equipo_de_computo'),
    i18n.t('hardcoded.pages_checkoutpage.i05_-_dados_troqueles_moldes'),
    i18n.t('hardcoded.pages_checkoutpage.i06_-_comunicaciones_telefonicas'),
    i18n.t('hardcoded.pages_checkoutpage.i07_-_comunicaciones_satelitales'),
    i18n.t('hardcoded.pages_checkoutpage.i08_-_otra_maquinaria_y'),
    i18n.t('hardcoded.pages_checkoutpage.d01_-_honorarios_medicos_dentales'),
    i18n.t('hardcoded.pages_checkoutpage.d02_-_gastos_medicos_por'),
    i18n.t('hardcoded.pages_checkoutpage.d03_-_gastos_funerales'),
    i18n.t('hardcoded.pages_checkoutpage.d04_-_donativos'),
    i18n.t('hardcoded.pages_checkoutpage.d05_-_intereses_reales_por'),
    i18n.t('hardcoded.pages_checkoutpage.d06_-_aportaciones_voluntarias_al'),
    i18n.t('hardcoded.pages_checkoutpage.d07_-_primas_por_seguros'),
    i18n.t('hardcoded.pages_checkoutpage.d08_-_gastos_de_transportacion'),
    i18n.t('hardcoded.pages_checkoutpage.d09_-_depositos_en_cuentas'),
    i18n.t('hardcoded.pages_checkoutpage.d10_-_pagos_por_servicios'),
    i18n.t('hardcoded.pages_checkoutpage.s01_-_sin_efectos_fiscales'),
    i18n.t('hardcoded.pages_checkoutpage.cp01_-_pagos'),
    i18n.t('hardcoded.pages_checkoutpage.cn01_-_nomina'),
];

const CFDI_PAYMENT_METHOD_OPTIONS = [
    i18n.t('hardcoded.pages_checkoutpage.pue_-_pago_en_una'),
    i18n.t('hardcoded.pages_checkoutpage.ppd_-_pago_en_parcialidades'),
];

const CFDI_PAYMENT_FORM_OPTIONS = [
    i18n.t('hardcoded.pages_checkoutpage.01_-_efectivo'),
    i18n.t('hardcoded.pages_checkoutpage.02_-_cheque_nominativo'),
    i18n.t('hardcoded.pages_checkoutpage.03_-_transferencia_electronica_de'),
    i18n.t('hardcoded.pages_checkoutpage.04_-_tarjeta_de_credito'),
    i18n.t('hardcoded.pages_checkoutpage.05_-_monedero_electronico'),
    i18n.t('hardcoded.pages_checkoutpage.06_-_dinero_electronico'),
    i18n.t('hardcoded.pages_checkoutpage.08_-_vales_de_despensa'),
    i18n.t('hardcoded.pages_checkoutpage.12_-_dacion_en_pago'),
    i18n.t('hardcoded.pages_checkoutpage.13_-_pago_por_subrogacion'),
    i18n.t('hardcoded.pages_checkoutpage.14_-_pago_por_consignacion'),
    i18n.t('hardcoded.pages_checkoutpage.15_-_condonacion'),
    i18n.t('hardcoded.pages_checkoutpage.17_-_compensacion'),
    i18n.t('hardcoded.pages_checkoutpage.23_-_novacion'),
    i18n.t('hardcoded.pages_checkoutpage.24_-_confusion'),
    i18n.t('hardcoded.pages_checkoutpage.25_-_remision_de_deuda'),
    i18n.t('hardcoded.pages_checkoutpage.26_-_prescripcion_o_caducidad'),
    i18n.t('hardcoded.pages_checkoutpage.27_-_a_satisfaccion_del'),
    i18n.t('hardcoded.pages_checkoutpage.28_-_tarjeta_de_debito'),
    i18n.t('hardcoded.pages_checkoutpage.29_-_tarjeta_de_servicios'),
    i18n.t('hardcoded.pages_checkoutpage.30_-_aplicacion_de_anticipos'),
    i18n.t('hardcoded.pages_checkoutpage.31_-_intermediario_pagos'),
    i18n.t('hardcoded.pages_checkoutpage.99_-_por_definir'),
];

const sanitizeValidationMessages = (validation) => {
    const safe = validation || { valid: true, errors: [], warnings: [] };
    const errors = (safe.errors || []).filter((msg) => !VEHICLE_VALIDATION_REGEX.test(String(msg || '')));
    const warnings = (safe.warnings || []).filter((msg) => !VEHICLE_VALIDATION_REGEX.test(String(msg || '')));
    return {
        ...safe,
        valid: errors.length === 0,
        errors,
        warnings,
    };
};

const getApiErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;

    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;

    if (Array.isArray(detail)) {
        return detail
            .map((entry) => {
                if (typeof entry === 'string') return entry;
                if (entry?.msg) return String(entry.msg);
                return null;
            })
            .filter(Boolean)
            .join(' | ') || fallback;
    }

    if (typeof detail === 'object') {
        if (detail.msg) return String(detail.msg);
        if (detail.message) return String(detail.message);
    }

    return fallback;
};

const CheckoutPage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cart, validateCart, cartTotals, loadCart } = useCart();
    const { showSuccess, showError, showInfo } = useNotification();

    const [expanded, setExpanded] = useState(t('hardcoded.pages_checkoutpage.products'));
    const [loading, setLoading] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);

    const [shippingForm, setShippingForm] = useState(initialShipping(user));
    const [billingForm, setBillingForm] = useState(initialBilling(user));
    const [customerNotes, setCustomerNotes] = useState('');

    const [saveShippingAddress, setSaveShippingAddress] = useState(true);
    const [saveBillingAddress, setSaveBillingAddress] = useState(true);

    const [savedShippingAddresses, setSavedShippingAddresses] = useState([]);
    const [savedBillingAddresses, setSavedBillingAddresses] = useState([]);
    const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
    const [selectedShippingAddressId, setSelectedShippingAddressId] = useState('');
    const [selectedBillingAddressId, setSelectedBillingAddressId] = useState('');

    const [checkoutSession, setCheckoutSession] = useState(null);
    const [paymentIntent, setPaymentIntent] = useState(null);

    const [cartValidation, setCartValidation] = useState({ valid: true, errors: [], warnings: [] });

    useEffect(() => {
        if (!cart?.items?.length) {
            navigate('/store');
            return;
        }
        void bootstrap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const bootstrap = async () => {
        const [validation, shippingAddresses, billingAddresses, savedPaymentMethods] = await Promise.all([
            validateCart(),
            ecommerceApi.orders.addresses.getAll('shipping').catch(() => ({ data: [] })),
            ecommerceApi.orders.addresses.getAll('billing').catch(() => ({ data: [] })),
            ecommerceApi.orders.paymentMethods.getAll().catch(() => ({ data: [] })),
        ]);

        setCartValidation(sanitizeValidationMessages(validation));
        setSavedShippingAddresses(shippingAddresses?.data || []);
        setSavedBillingAddresses(billingAddresses?.data || []);
        setSavedPaymentMethods(savedPaymentMethods?.data || []);

        const defaultShipping = (shippingAddresses?.data || []).find((a) => a.is_default) || (shippingAddresses?.data || [])[0];
        const defaultBilling = (billingAddresses?.data || []).find((a) => a.is_default) || (billingAddresses?.data || [])[0];

        if (defaultShipping) {
            setSelectedShippingAddressId(defaultShipping.id);
            setShippingForm((prev) => ({ ...prev, ...defaultShipping }));
        }
        if (defaultBilling) {
            setSelectedBillingAddressId(defaultBilling.id);
            setBillingForm((prev) => ({ ...prev, ...defaultBilling }));
        }
    };

    const sectionSummary = useMemo(() => ({
        products: `${cartTotals?.itemCount || 0} item(s) - ${cartHelpers.formatCurrency(cartTotals?.total || 0)}`,
        shipping: shippingForm.address_line_1 ? `${shippingForm.city}, ${shippingForm.state}` : t('hardcoded.components_admin_ordermanagement.pendiente'),
        billing: billingForm.business_name ? `${billingForm.business_name} (${billingForm.rfc || t('hardcoded.pages_checkoutpage.rfc_pendiente')})` : t('hardcoded.components_admin_ordermanagement.pendiente'),
        payment: checkoutSession?.id ? t('hardcoded.pages_checkoutpage.listo_para_pagar') : t('hardcoded.components_admin_ordermanagement.pendiente'),
        confirmation: checkoutSession?.id ? t('hardcoded.pages_checkoutpage.en_progreso') : t('hardcoded.components_admin_ordermanagement.pendiente'),
    }), [cartTotals, shippingForm, billingForm, checkoutSession]);

    const validateForm = (form, requiredFields) => {
        for (const field of requiredFields) {
            if (!String(form[field] || '').trim()) {
                showError(t('hardcoded.pages_checkoutpage.falta_el_campo_obligatorio_field', { field: field }));
                return false;
            }
        }

        const country = String(form.country || '').toLowerCase();
        if (country.includes('mex') && form.rfc) {
            const rfc = String(form.rfc).toUpperCase().trim();
            const moral = /^[A-Z&Ñ]{3}\d{6}[A-Z0-9]{3}$/;
            const fisica = /^[A-Z&Ñ]{4}\d{6}[A-Z0-9]{3}$/;
            if (!(moral.test(rfc) || fisica.test(rfc))) {
                showError(t('hardcoded.pages_checkoutpage.rfc_invalido_para_mexico'));
                return false;
            }
        }

        return true;
    };

    const createCheckoutSession = async () => {
        if (!cartValidation.valid) {
            showError(t('hardcoded.pages_checkoutpage.el_carrito_tiene_errores_que'));
            return;
        }
        if (!validateForm(shippingForm, REQUIRED_SHIPPING)) return;
        if (!validateForm(billingForm, REQUIRED_BILLING)) return;

        setLoading(true);
        try {
            const response = await ecommerceApi.orders.createCheckoutSession({
                shipping_address: shippingForm,
                billing_address: billingForm,
                customer_notes: customerNotes || null,
                save_shipping_address: saveShippingAddress,
                save_billing_address: saveBillingAddress,
            });

            setCheckoutSession(response.data);
            setExpanded(t('hardcoded.pages_checkoutpage.payment'));
            showSuccess(t('hardcoded.pages_checkoutpage.checkout_listo_procede_con_el'));
        } catch (error) {
            showError(getApiErrorMessage(error, t('hardcoded.pages_checkoutpage.no_se_pudo_iniciar_el')));
        } finally {
            setLoading(false);
        }
    };

    const createPaymentIntent = async () => {
        if (!checkoutSession?.id) {
            showError(t('hardcoded.pages_checkoutpage.primero_completa_las_secciones_de'));
            return;
        }

        setPaymentLoading(true);
        try {
            const response = await ecommerceApi.orders.createCheckoutPaymentIntent(checkoutSession.id);
            setPaymentIntent(response.data);
        } catch (error) {
            showError(getApiErrorMessage(error, t('hardcoded.pages_checkoutpage.no_se_pudo_preparar_el')));
        } finally {
            setPaymentLoading(false);
        }
    };

    const renderAddressSelect = ({ list, selectedId, setSelectedId, onSelect, label }) => (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={selectedId}
                label={label}
                onChange={(e) => {
                    const id = e.target.value;
                    setSelectedId(id);
                    const found = list.find((entry) => entry.id === id);
                    if (found) onSelect(found);
                }}
            >
                <MenuItem value="">{i18n.t('hardcoded.pages_checkoutpage.capturar_nueva_direccion')}</MenuItem>
                {list.map((addr) => (
                    <MenuItem key={addr.id} value={addr.id}>
                        {addr.label || `${addr.address_line_1} - ${addr.city}`}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const addressSectionSx = {
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        mb: 2,
    };

    const renderSearchSelect = ({ label, value, options, onChange, required = false }) => (
        <Autocomplete
            fullWidth
            size="small"
            options={options}
            value={value || null}
            onChange={(_, newValue) => onChange(newValue || '')}
            renderInput={(params) => (
                <TextField {...params} label={label} required={required} />
            )}
            isOptionEqualToValue={(option, selected) => option === selected}
        />
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                Checkout
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Accordion expanded={expanded === 'products'} onChange={() => setExpanded(expanded === 'products' ? false : 'products')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CartIcon color="primary" />
                                <Typography variant="h6">{i18n.t('hardcoded.pages_checkoutpage.revisar_productos')}</Typography>
                                <Typography variant="body2" color="text.secondary">{sectionSummary.products}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {!!cartValidation.errors?.length && (
                                <Alert severity="error" sx={{ mb: 2 }}>{cartValidation.errors.join(' | ')}</Alert>
                            )}
                            {!!cartValidation.warnings?.length && (
                                <Alert severity="warning" sx={{ mb: 2 }}>{cartValidation.warnings.join(' | ')}</Alert>
                            )}

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                {(cart?.items || []).map((item) => (
                                    <CartItemLine key={item.id} item={item} />
                                ))}
                            </Paper>
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="contained" onClick={() => setExpanded('shipping')}>{i18n.t('hardcoded.components_auth_joincompany.continuar')}</Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={expanded === 'shipping'} onChange={() => setExpanded(expanded === 'shipping' ? false : 'shipping')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <LocationIcon color="primary" />
                                <Typography variant="h6">{t('hardcoded.pages_checkoutpage.direccion_de_envio')}</Typography>
                                <Typography variant="body2" color="text.secondary">{sectionSummary.shipping}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={addressSectionSx}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                    {t('hardcoded.pages_checkoutpage.direcciones_guardadas_de_envio')}</Typography>
                                {renderAddressSelect({
                                    list: savedShippingAddresses,
                                    selectedId: selectedShippingAddressId,
                                    setSelectedId: setSelectedShippingAddressId,
                                    onSelect: (addr) => setShippingForm((prev) => ({ ...prev, ...addr })),
                                    label: t('hardcoded.pages_checkoutpage.selecciona_direccion_de_envio'),
                                })}

                                <Grid container spacing={2}>
                                    {[
                                        ['first_name', 'Nombre'],
                                        ['last_name', t('hardcoded.pages_checkoutpage.apellidos')],
                                        ['address_line_1', 'Direccion 1'],
                                        ['address_line_2', 'Direccion 2'],
                                        ['city', t('settings.company.city')],
                                        [t('hardcoded.components_auth_register.state'), t('gps.table.status')],
                                        ['postal_code', t('hardcoded.pages_checkoutpage.codigo_postal')],
                                    ].map(([field, label]) => (
                                        <Grid item xs={12} sm={field.includes('address') ? 12 : 6} key={field}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={label}
                                                value={shippingForm[field] || ''}
                                                onChange={(e) => setShippingForm((prev) => ({ ...prev, [field]: e.target.value }))}
                                            />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <PhoneInput
                                            value={shippingForm.phone}
                                            onChange={(value) => setShippingForm((prev) => ({ ...prev, phone: value }))}
                                            country={shippingForm.country}
                                            onCountryChange={(country) => setShippingForm((prev) => {
                                                if ((prev.country || '').trim().toLowerCase() === (country || '').trim().toLowerCase()) {
                                                    return prev;
                                                }
                                                return { ...prev, country };
                                            })}
                                            label={t('hardcoded.pages_checkoutpage.telefono')}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <FormControlLabel
                                control={<Checkbox checked={saveShippingAddress} onChange={(e) => setSaveShippingAddress(e.target.checked)} />}
                                label={t('hardcoded.pages_checkoutpage.guardar_direccion_de_envio')}
                                sx={{ mt: 1 }}
                            />

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="contained" onClick={() => setExpanded('billing')}>{i18n.t('hardcoded.components_auth_joincompany.continuar')}</Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={expanded === 'billing'} onChange={() => setExpanded(expanded === 'billing' ? false : 'billing')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <BillingIcon color="primary" />
                                <Typography variant="h6">{t('hardcoded.pages_checkoutpage.direccion_de_facturacion')}</Typography>
                                <Typography variant="body2" color="text.secondary">{sectionSummary.billing}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={addressSectionSx}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                    {t('hardcoded.pages_checkoutpage.direcciones_guardadas_de_facturacion')}</Typography>
                                {renderAddressSelect({
                                    list: savedBillingAddresses,
                                    selectedId: selectedBillingAddressId,
                                    setSelectedId: setSelectedBillingAddressId,
                                    onSelect: (addr) => setBillingForm((prev) => ({ ...prev, ...addr })),
                                    label: t('hardcoded.pages_checkoutpage.selecciona_direccion_de_facturacion'),
                                })}

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('hardcoded.pages_checkoutpage.tipo_de_persona')}</InputLabel>
                                            <Select
                                                value={billingForm.person_type}
                                                label={t('hardcoded.pages_checkoutpage.tipo_de_persona')}
                                                onChange={(e) => setBillingForm((prev) => ({ ...prev, person_type: e.target.value }))}
                                            >
                                                <MenuItem value="fisica">{i18n.t('hardcoded.pages_checkoutpage.fisica')}</MenuItem>
                                                <MenuItem value="moral">{i18n.t('hardcoded.pages_checkoutpage.moral')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t('hardcoded.pages_checkoutpage.nombre_o_razon_social')}
                                            value={billingForm.business_name || ''}
                                            onChange={(e) => setBillingForm((prev) => ({ ...prev, business_name: e.target.value }))}
                                        />
                                    </Grid>

                                    {[
                                        ['rfc', 'RFC'],
                                        ['address_line_1', 'Direccion 1'],
                                        ['address_line_2', 'Direccion 2'],
                                        ['city', t('settings.company.city')],
                                        [t('hardcoded.components_auth_register.state'), t('gps.table.status')],
                                        ['postal_code', t('hardcoded.pages_checkoutpage.codigo_postal')],
                                    ].map(([field, label]) => (
                                        <Grid item xs={12} sm={['city', 'state', 'postal_code'].includes(field) ? 4 : 6} key={field}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={label}
                                                value={billingForm[field] || ''}
                                                onChange={(e) => setBillingForm((prev) => ({ ...prev, [field]: e.target.value }))}
                                            />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12} sm={4}>
                                        {renderSearchSelect({
                                            label: t('hardcoded.pages_checkoutpage.uso_de_cfdi'),
                                            value: billingForm.cfdi_use,
                                            options: CFDI_USE_OPTIONS,
                                            onChange: (newValue) => setBillingForm((prev) => ({ ...prev, cfdi_use: newValue })),
                                            required: true,
                                        })}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        {renderSearchSelect({
                                            label: t('hardcoded.pages_checkoutpage.cfdi_metodo_de_pago'),
                                            value: billingForm.cfdi_payment_method,
                                            options: CFDI_PAYMENT_METHOD_OPTIONS,
                                            onChange: (newValue) => setBillingForm((prev) => ({ ...prev, cfdi_payment_method: newValue })),
                                            required: true,
                                        })}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        {renderSearchSelect({
                                            label: t('hardcoded.pages_checkoutpage.cfdi_forma_de_pago'),
                                            value: billingForm.cfdi_payment_form,
                                            options: CFDI_PAYMENT_FORM_OPTIONS,
                                            onChange: (newValue) => setBillingForm((prev) => ({ ...prev, cfdi_payment_form: newValue })),
                                            required: true,
                                        })}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <PhoneInput
                                            value={billingForm.phone}
                                            onChange={(value) => setBillingForm((prev) => ({ ...prev, phone: value }))}
                                            country={billingForm.country}
                                            onCountryChange={(country) => setBillingForm((prev) => {
                                                if ((prev.country || '').trim().toLowerCase() === (country || '').trim().toLowerCase()) {
                                                    return prev;
                                                }
                                                return { ...prev, country };
                                            })}
                                            label={t('hardcoded.pages_checkoutpage.telefono')}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                label={t('hardcoded.pages_checkoutpage.notas_del_pedido')}
                                value={customerNotes}
                                onChange={(e) => setCustomerNotes(e.target.value)}
                                sx={{ mt: 2 }}
                            />

                            <FormControlLabel
                                control={<Checkbox checked={saveBillingAddress} onChange={(e) => setSaveBillingAddress(e.target.checked)} />}
                                label={t('hardcoded.pages_checkoutpage.guardar_direccion_de_facturacion')}
                                sx={{ mt: 1 }}
                            />

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="contained" onClick={createCheckoutSession} disabled={loading}>
                                    {loading ? <CircularProgress size={18} color="inherit" /> : t('hardcoded.pages_checkoutpage.continuar_a_pago')}
                                </Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={expanded === 'payment'} onChange={() => setExpanded(expanded === 'payment' ? false : 'payment')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PaymentIcon color="primary" />
                                <Typography variant="h6">{t('hardcoded.pages_checkoutpage.metodo_de_pago')}</Typography>
                                <Typography variant="body2" color="text.secondary">{sectionSummary.payment}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {!paymentIntent ? (
                                <Button variant="contained" onClick={createPaymentIntent} disabled={!checkoutSession || paymentLoading}>
                                    {paymentLoading ? <CircularProgress size={18} color="inherit" /> : t('hardcoded.pages_checkoutpage.preparar_pago_con_tarjeta')}
                                </Button>
                            ) : (
                                <Stack spacing={2}>
                                    {savedPaymentMethods.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>{i18n.t('hardcoded.pages_checkoutpage.tarjetas_guardadas')}</Typography>
                                            <Stack spacing={1}>
                                                {savedPaymentMethods.map((method) => (
                                                    <Paper
                                                        key={method.id}
                                                        sx={{
                                                            p: 2,
                                                            cursor: 'pointer',
                                                            border: '2px solid',
                                                            borderColor: 'transparent',
                                                            '&:hover': { borderColor: 'primary.main' },
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Typography variant="body2">
                                                                {method.brand?.toUpperCase()} {t('hardcoded.pages_checkoutpage.terminada_en')}{method.last4}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {method.exp_month}/{method.exp_year}
                                                            </Typography>
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('hardcoded.pages_checkoutpage.o_usa_una_tarjeta_nueva')}</Typography>
                                        </Box>
                                    )}
                                    <Elements stripe={stripePromise}>
                                        <StripePaymentForm
                                            checkoutSession={checkoutSession}
                                            paymentIntent={paymentIntent}
                                            onSuccess={async () => {
                                                showSuccess(t('hardcoded.pages_checkoutpage.pago_confirmado_tu_orden_fue'));
                                                await loadCart();
                                                navigate('/orders');
                                            }}
                                            onError={(message) => showError(message)}
                                            onInfo={(message) => showInfo(message)}
                                        />
                                    </Elements>
                                </Stack>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={expanded === 'confirmation'} onChange={() => setExpanded(expanded === 'confirmation' ? false : 'confirmation')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <ConfirmIcon color="primary" />
                                <Typography variant="h6">{t('pages_checkoutpage.accordion_confirmation')}</Typography>
                                <Typography variant="body2" color="text.secondary">{sectionSummary.confirmation}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" color="text.secondary">
                                {t('hardcoded.pages_checkoutpage.al_completar_el_pago_se')}</Typography>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom>{i18n.t('vehicleDetail.tabs.overview')}</Typography>

                        <CartOrderSummary
                            cartTotals={cartTotals}
                            totalSavings={cartHelpers.calculateSavings(cart)}
                            showSavings
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

const StripePaymentForm = ({ checkoutSession, paymentIntent, onSuccess, onError, onInfo }) => {
  const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [saveCard, setSaveCard] = useState(false);

    const pollOrderSync = async (tries = 25) => {
        for (let i = 0; i < tries; i += 1) {
            try {
                const session = await ecommerceApi.orders.getCheckoutSession(checkoutSession.id);
                if (session?.data?.status === 'paid') {
                    return true;
                }
            } catch (e) {
                // no-op
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 700));
        }
        return false;
    };

    const savePaymentMethod = async (setupIntentSecret) => {
        try {
            const cardElement = elements.getElement(CardElement);
            const { error, setupIntent } = await stripe.confirmCardSetup(setupIntentSecret, {
                payment_method: { card: cardElement },
            });

            if (error) {
                console.warn(t('hardcoded.pages_checkoutpage.advertencia_al_guardar_tarjeta'), error.message);
                return null;
            }

            const paymentMethodId = typeof setupIntent?.payment_method === 'string'
                ? setupIntent.payment_method
                : setupIntent?.payment_method?.id;

            if (!paymentMethodId) {
                console.warn(t('hardcoded.pages_checkoutpage.no_se_obtuvo_id_de'));
                return null;
            }

            // Retrieve payment method details
            const pmDetails = setupIntent?.payment_method;
            const brand = pmDetails?.card?.brand || 'unknown';
            const last4 = pmDetails?.card?.last4 || '';
            const expMonth = pmDetails?.card?.exp_month || null;
            const expYear = pmDetails?.card?.exp_year || null;

            // Save to backend
            await ecommerceApi.orders.paymentMethods.create({
                provider: 'stripe',
                provider_payment_method_id: paymentMethodId,
                brand,
                last4,
                exp_month: expMonth,
                exp_year: expYear,
                is_default: true,
            });

            return paymentMethodId;
        } catch (err) {
            console.warn(t('hardcoded.pages_checkoutpage.error_guardando_metodo_de_pago'), err.message);
            return null;
        }
    };

    const handlePay = async (event) => {
        event.preventDefault();
        if (!stripe || !elements || !paymentIntent?.client_secret) {
            onError(t('hardcoded.pages_checkoutpage.pago_no_disponible_en_este'));
            return;
        }

        setProcessing(true);
        try {
            const cardElement = elements.getElement(CardElement);
            const { error, paymentIntent: confirmed } = await stripe.confirmCardPayment(paymentIntent.client_secret, {
                payment_method: { card: cardElement },
            });

            if (error) {
                onError(error.message || t('hardcoded.pages_checkoutpage.error_al_procesar_el_pago'));
                setProcessing(false);
                return;
            }

            if (confirmed?.status === 'succeeded' || confirmed?.status === 'processing') {
                onInfo(t('hardcoded.pages_checkoutpage.pago_recibido_confirmando_orden'));

                // Save payment method if requested (non-blocking)
                if (saveCard && paymentIntent?.setup_future_usage) {
                    savePaymentMethod(paymentIntent.client_secret).catch((err) => {
                        console.warn('Error background saving card:', err);
                    });
                }

                try {
                    await ecommerceApi.orders.finalizeCheckoutSession(checkoutSession.id, confirmed.id);
                } catch (err) {
                    // fallback a polling por webhook
                }
                const synced = await pollOrderSync();
                if (synced) {
                    onSuccess();
                } else {
                    onError(t('hardcoded.pages_checkoutpage.el_pago_se_registro_pero'));
                }
                setProcessing(false);
                return;
            }

            onError(t('hardcoded.pages_checkoutpage.estado_de_pago_no_esperado', { confirmedstatus: confirmed?.status || 'desconocido' }));
        } catch (err) {
            onError(err.message || t('hardcoded.pages_checkoutpage.error_inesperado_de_pago'));
        }
        setProcessing(false);
    };

    return (
        <Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                <CardElement
                    options={{
                        style: {
                            base: {
                                fontSize: '16px',
                                color: '#2b2b2b',
                                '::placeholder': { color: '#9a9a9a' },
                            },
                        },
                    }}
                />
            </Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                    />
                }
                label={t('hardcoded.pages_checkoutpage.guardar_esta_tarjeta_para_futuros')}
                sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={handlePay} disabled={processing || !stripe}>
                {processing ? <CircularProgress size={18} color="inherit" /> : t('hardcoded.pages_checkoutpage.pagar_carthelpersform', { cartHelpersform: cartHelpers.formatCurrency(checkoutSession?.total || 0) })}
            </Button>
        </Box>
    );
};

export default CheckoutPage;
